import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";

/**
 * Click-to-enter gate. Covers the viewport until clicked, then plays a reveal
 * animation and resolves so the caller can hydrate the profile + audio
 * (audio autoplay requires a user gesture; the gate click is that gesture).
 *
 * The `animation` choice maps to a CSS class that drives the reveal keyframes.
 * Per-effect speed overrides apply via the `--gate-duration` custom property.
 */
export function mountGate(
  cfg: ProfileConfig["gate"],
  motion: ProfileConfig["motion"],
  options: { autofocus?: boolean } = { autofocus: true },
): { element: HTMLElement; opened: Promise<void> } {
  if (!cfg.enabled) {
    // Gate disabled — return a no-op placeholder that's already resolved.
    const stub = el("div", { class: "gate gate--disabled", "aria-hidden": "true" });
    return { element: stub, opened: Promise.resolve() };
  }

  const speedMul = motion.speedOverrides["gateAnimation"] ?? 1;
  const duration =
    speedMul === 0 ? 0 : Math.max(100, cfg.durationMs / speedMul);

  // Project every typography + chrome override into CSS custom properties
  // so the stylesheet rules pick them up without prop-drilling.
  const styleParts = [
    `--gate-duration:${duration}ms`,
    `--gate-text-color:${cfg.textColor}`,
    `--gate-pill-bg:${cfg.pillBgColor}`,
    `--gate-pill-bg-alpha:${cfg.pillBgOpacity}`,
    `--gate-pill-border:${cfg.pillBorderColor}`,
    `--gate-pill-border-width:${cfg.pillBorderWidthPx}px`,
    `--gate-text-size:${cfg.textSizeRem}rem`,
    `--gate-letter-spacing:${cfg.letterSpacingEm}em`,
    `--gate-font-weight:${cfg.fontWeight}`,
    `--gate-backdrop-blur:${cfg.backdropBlurPx}px`,
  ];
  const gate = el("div", {
    class: `gate gate--${cfg.animation}`,
    role: "button",
    tabindex: "0",
    "aria-label": "Click to enter",
    style: styleParts.join(";"),
  });

  const inner = el("div", { class: "gate__inner" });
  if (cfg.tagline) {
    inner.append(el("span", { class: "gate__tagline" }, [cfg.tagline]));
  }
  if (cfg.emoji) {
    inner.append(el("span", { class: "gate__emoji" }, [cfg.emoji]));
  }
  inner.append(el("span", { class: "gate__text" }, [cfg.text]));
  gate.append(inner);

  const opened = new Promise<void>((resolve) => {
    const onOpen = () => {
      gate.removeEventListener("click", onOpen);
      gate.removeEventListener("keydown", onKey);
      // Fire a synchronous user-gesture event on document. The audio
      // player (and any other component that needs to prime a play()
      // call in a user-gesture context — iOS Safari autoplay policy is
      // strict about this) listens for this event and invokes the
      // priming call SYNCHRONOUSLY, before any setTimeout or promise
      // resolution can break the gesture context chain.
      document.dispatchEvent(new CustomEvent("bio:user-gesture"));
      gate.classList.add("gate--opening");
      // Wait for the reveal animation, then remove from flow.
      window.setTimeout(() => {
        gate.remove();
        resolve();
      }, duration);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        onOpen();
      }
    };
    gate.addEventListener("click", onOpen);
    gate.addEventListener("keydown", onKey);
  });

  // Autofocus so keyboard users can press Enter to open without hunting.
  // Skipped inside admin preview — focus-stealing breaks the admin text
  // inputs as the user types gate settings.
  if (options.autofocus !== false) {
    requestAnimationFrame(() => gate.focus({ preventScroll: true }));
  }

  return { element: gate, opened };
}
